import type { IDokumAlasim, IKalipMalzeme } from '../fiko-sph-sivi/FikoDokumMalzemeDB';
import type { IYollukEleman, IMethodingSonuc, IBesleyiciModulKontrol } from './IDokumKontrol';

/**
 * Kum döküm yöntemleme (methoding) hesapları — saf fonksiyonlar.
 * Mevcut FikoDokumMalzemeDB alaşım/kum verisini kullanır (yoğunluk, çekme,
 * döküm sıcaklığı, akışkanlık). Çıktı: ağırlık, dolum süresi, giriş hızı,
 * besleyici modül yeterliliği (Chvorinov), verim (yield %).
 */

/** Birleştirilmiş geometriden işaretli hacim (cm³). positions mm cinsinden. */
export function parcaHacimCm3(positions: Float32Array, indices: Uint32Array): number {
  let v6 = 0;
  for (let t = 0; t < indices.length; t += 3) {
    const a = indices[t] * 3, b = indices[t + 1] * 3, c = indices[t + 2] * 3;
    const ax = positions[a], ay = positions[a + 1], az = positions[a + 2];
    const bx = positions[b], by = positions[b + 1], bz = positions[b + 2];
    const cx = positions[c], cy = positions[c + 1], cz = positions[c + 2];
    // a · (b × c)
    v6 += ax * (by * cz - bz * cy) - ay * (bx * cz - bz * cx) + az * (bx * cy - by * cx);
  }
  const hacimMm3 = Math.abs(v6) / 6;
  return hacimMm3 / 1000; // mm³ → cm³
}

/** Silindir (besleyici) hacmi cm³ — çap/yükseklik mm */
function silindirHacimCm3(capMm: number, yukseklikMm: number): number {
  const r = capMm / 2 / 10, h = yukseklikMm / 10; // cm
  return Math.PI * r * r * h;
}

/** Silindir modülü M = V/A (cm) — yan yüzey + üst (alt besleme yüzeyi sayılmaz) */
function silindirModulCm(capMm: number, yukseklikMm: number): number {
  const d = capMm / 10, h = yukseklikMm / 10; // cm
  const r = d / 2;
  const V = Math.PI * r * r * h;
  const A = Math.PI * d * h + Math.PI * r * r; // yan + üst
  return A > 0 ? V / A : 0;
}

export interface IMethodingGirdi {
  positions: Float32Array;
  indices: Uint32Array;
  alasim: IDokumAlasim;
  kum: IKalipMalzeme;
  yolluklar: IYollukEleman[];
  /** Beslenecek kritik kesit modülü (cm) — sıcak nokta kalınlığından */
  hedefKesitModulCm: number;
  /** Alan×kalınlık yöntemiyle hesaplanmış malzeme hacmi (cm³). Verilirse işaretli-hacim yerine bu kullanılır. */
  hacimCm3Override?: number;
}

/** Alaşıma göre güvenli giriş hızı limiti (m/s) — Al düşük (oksit), demir yüksek */
function girisHiziLimiti(alasim: IDokumAlasim): number {
  const k = alasim.kategori as string;
  if (k === 'aluminyum') return 0.5;
  if (k === 'magnezyum') return 0.4;
  return 1.0; // demir/çelik/bronz daha toleranslı
}

/** Alaşım yüzey gerilimi (N/m) — Weber sayısı için yaklaşık değerler */
function yuzeyGerilimi(alasim: IDokumAlasim): number {
  const k = alasim.kategori as string;
  if (k === 'aluminyum') return 0.9;
  if (k === 'magnezyum') return 0.55;
  if (k === 'cinko') return 0.78;
  if (k === 'bakir' || k === 'bronz' || k === 'pirinc') return 1.3;
  return 1.8; // demir/çelik
}

export function methodingHesapla(g: IMethodingGirdi): IMethodingSonuc {
  const { alasim, kum, yolluklar } = g;
  const yogunlukGcm3 = alasim.yogunlukKati / 1000; // kg/m³ → g/cm³

  const parcaHacimCm3v = (g.hacimCm3Override != null && g.hacimCm3Override > 0)
    ? g.hacimCm3Override
    : parcaHacimCm3(g.positions, g.indices);
  const parcaKutleG = parcaHacimCm3v * yogunlukGcm3;
  const parcaKutleKg = parcaKutleG / 1000;

  const girisler = yolluklar.filter(y => y.tip === 'dolum_girisi');
  const besleyiciler = yolluklar.filter(y => y.tip === 'besleyici');

  const toplamDebiGramSn = girisler.reduce((t, y) => t + (y.debiGramSn || 0), 0);
  const dolumSuresiSn = toplamDebiGramSn > 0 ? parcaKutleG / toplamDebiGramSn : 0;

  // Giriş hızı: Q(cm³/s)=debi/yoğunluk, A=Σπ(cap/2)²; v=Q/A
  let toplamAlanCm2 = 0;
  for (const y of girisler) { const r = y.capMm / 2 / 10; toplamAlanCm2 += Math.PI * r * r; }
  const QcmSn = toplamDebiGramSn / Math.max(yogunlukGcm3, 1e-6); // cm³/s
  const girisHiziMs = toplamAlanCm2 > 0 ? (QcmSn / toplamAlanCm2) / 100 : 0; // cm/s → m/s
  const girisHiziLimitMs = girisHiziLimiti(alasim);
  const girisHiziUygun = girisHiziMs > 0 && girisHiziMs <= girisHiziLimitMs;

  // Weber sayısı We = ρ·v²·L/σ (yüzey türbülansı/oksit sürüklenme). L = giriş çapı (m).
  const sigma = yuzeyGerilimi(alasim);
  const Lkar = (girisler.length ? girisler.reduce((t, y) => t + y.capMm, 0) / girisler.length : 20) / 1000; // m
  const rhoKg = alasim.yogunlukKati; // kg/m³
  const weberSayisi = sigma > 0 ? (rhoKg * girisHiziMs * girisHiziMs * Lkar) / sigma : 0;
  const oksitRiski = weberSayisi > 100 || (girisHiziMs > girisHiziLimitMs);

  // Hacim/ağırlık: besleyici + yolluk
  let besleyiciHacimCm3 = 0;
  for (const b of besleyiciler) besleyiciHacimCm3 += silindirHacimCm3(b.capMm, b.yukseklikMm);
  let yollukHacimCm3 = 0;
  for (const y of yolluklar) if (y.tip !== 'besleyici') yollukHacimCm3 += silindirHacimCm3(y.capMm, y.yukseklikMm);

  const dokulenHacimCm3 = parcaHacimCm3v + besleyiciHacimCm3 + yollukHacimCm3;
  const toplamDokulenKg = dokulenHacimCm3 * yogunlukGcm3 / 1000;
  const verimYuzde = dokulenHacimCm3 > 0 ? (parcaHacimCm3v / dokulenHacimCm3) * 100 : 0;

  // Besleyici modül kontrolü (Chvorinov): M_besleyici ≥ 1.2 × M_kesit
  const hedef = g.hedefKesitModulCm;
  const besleyiciModul: IBesleyiciModulKontrol[] = besleyiciler.map(b => {
    const m = silindirModulCm(b.capMm, b.yukseklikMm);
    const yeterli = m >= hedef * 1.2;
    return {
      id: b.id,
      modulBesleyiciCm: Math.round(m * 100) / 100,
      modulHedefCm: Math.round(hedef * 1.2 * 100) / 100,
      yeterli,
      mesaj: yeterli
        ? `Besleyici modülü yeterli (${m.toFixed(2)} ≥ ${(hedef * 1.2).toFixed(2)} cm)`
        : `Besleyici küçük: modül ${m.toFixed(2)} < gerekli ${(hedef * 1.2).toFixed(2)} cm → çekme riski, çapı büyüt`,
    };
  });

  const uyarilar: string[] = [];
  if (girisler.length === 0) uyarilar.push('Dolum girişi yok — debi/dolum süresi hesaplanamadı.');
  else if (!girisHiziUygun && girisHiziMs > 0)
    uyarilar.push(`Giriş hızı ${girisHiziMs.toFixed(2)} m/s > ${girisHiziLimitMs} m/s limit — oksit/türbülans riski, giriş kesitini büyüt veya debiyi düşür.`);
  if (besleyiciler.length === 0) uyarilar.push('Besleyici yok — çekme boşluğu riski, sıcak noktalara besleyici ekle.');
  else if (besleyiciModul.some(b => !b.yeterli)) uyarilar.push('Bazı besleyiciler modül olarak yetersiz — çapı/yüksekliği artır.');
  if (verimYuzde > 0 && verimYuzde < 45) uyarilar.push(`Düşük verim (%${verimYuzde.toFixed(0)}) — yolluk/besleyici fazla, hurda artar.`);

  return {
    alasimId: alasim.id, alasimAd: alasim.ad,
    kumId: kum.id, kumAd: kum.ad,
    dokumSicaklikMinC: alasim.T_dokum_min, dokumSicaklikMaxC: alasim.T_dokum_max,
    yogunlukGcm3: Math.round(yogunlukGcm3 * 1000) / 1000,
    cekmeYuzde: Math.round(alasim.cekmeOrani * 1000) / 10, // β → %
    parcaHacimCm3: Math.round(parcaHacimCm3v * 10) / 10,
    parcaKutleKg: Math.round(parcaKutleKg * 1000) / 1000,
    toplamDebiGramSn,
    dolumSuresiSn: Math.round(dolumSuresiSn * 100) / 100,
    girisHiziMs: Math.round(girisHiziMs * 1000) / 1000,
    girisHiziLimitMs, girisHiziUygun,
    weberSayisi: Math.round(weberSayisi),
    oksitRiski,
    besleyiciHacimCm3: Math.round(besleyiciHacimCm3 * 10) / 10,
    yollukHacimCm3: Math.round(yollukHacimCm3 * 10) / 10,
    toplamDokulenKg: Math.round(toplamDokulenKg * 1000) / 1000,
    verimYuzde: Math.round(verimYuzde * 10) / 10,
    besleyiciModul,
    uyarilar,
  };
}
